import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApi } from "../../../service/ApiContext";
import InterviewHubApiService from "../service/InterviewHubApiService";
import {
  AnswerRequest,
  AnswerResponse,
  InterviewHistoryItem,
} from "../data/interface/InterviewHubInterface";
import { useDirectTranscribe } from "../service/useDirectTranscribe";
import Loader from "../../../utils/Utils";
import "../styles/InterviewSessionStyles.css";

// Import detection hooks
import { useFaceDetection } from "../../../hook/useFaceDetection";
import { useObjectDetection } from "../../../hook/useObjectDetection";

// ─── Types ───────────────────────────────────────────────────────────────────
interface ChatMessage {
  id: string;
  role: "bot" | "user";
  text: string;
  timestamp: Date;
}

interface InterviewSessionPageProps {
  id?: string;
  isResume?: boolean;
}

interface ProctorAPIResponse {
  malpractice_detected: boolean;
  details: string | null;
}

type IcebreakerState = "polling" | "ready" | "failed";
type WsStatus = "idle" | "connecting" | "connected" | "error";
type MalpracticeType =
  | "multiple_faces"
  | "head_position"
  | "looking_away"
  | "no_face"
  | "inactivity"
  | "prohibited_object"
  | "api_malpractice";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const makeId = () => Math.random().toString(36).slice(2);
const CAPTURE_INTERVAL_MS = 20000;
const NORMAL_API_INTERVAL_MS = 10000;
const INTENSIVE_API_INTERVAL_MS = 2000;
const INTENSIVE_MODE_DURATION_MS = 60000;

const formatTime = (d: Date) =>
  d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const historyToMessages = (history: InterviewHistoryItem[]): ChatMessage[] =>
  history.map((item) => ({
    id: makeId(),
    role: item.role === "bot" ? "bot" : "user",
    text: item.content,
    timestamp: new Date(),
  }));

// ─── Global Camera Manager (Singleton pattern) ──────────────────────────────────
class CameraManager {
  private static instance: CameraManager;
  private stream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private canvasElement: HTMLCanvasElement | null = null;
  private intervalId: number | null = null;
  private isActive = false;
  private onCaptureCallback:
    | ((imageData: string, timestamp: string) => void)
    | null = null;
  private onPermissionDeniedCallback: (() => void) | null = null;

  static getInstance(): CameraManager {
    if (!CameraManager.instance) {
      CameraManager.instance = new CameraManager();
    }
    return CameraManager.instance;
  }

  setCallbacks(
    onCapture: (imageData: string, timestamp: string) => void,
    onPermissionDenied: () => void,
  ) {
    this.onCaptureCallback = onCapture;
    this.onPermissionDeniedCallback = onPermissionDenied;
  }

  async start(
    videoRef: React.RefObject<HTMLVideoElement>,
    canvasRef: React.RefObject<HTMLCanvasElement>,
  ) {
    if (this.isActive) return;

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user",
        },
        audio: false,
      });

      this.videoElement = videoRef.current;
      this.canvasElement = canvasRef.current;

      if (!this.videoElement) throw new Error("Video element not found");

      this.videoElement.srcObject = this.stream;
      this.isActive = true;

      this.videoElement.onplaying = () => {
        setTimeout(() => {
          this.captureImage();
          if (this.intervalId) clearInterval(this.intervalId);
          this.intervalId = window.setInterval(
            () => this.captureImage(),
            CAPTURE_INTERVAL_MS,
          );
        }, 1500);
      };
    } catch (err) {
      console.error("Camera error:", err);
      this.onPermissionDeniedCallback?.();
    }
  }

  private captureImage() {
    if (!this.isActive || !this.videoElement || !this.canvasElement) return;

    const video = this.videoElement;
    const canvas = this.canvasElement;

    if (
      video.readyState < 2 ||
      video.videoWidth === 0 ||
      video.videoHeight === 0
    )
      return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/webp", 0.85);

    if (dataUrl.length < 5000) return;

    this.onCaptureCallback?.(dataUrl, new Date().toISOString());
  }

  getStream(): MediaStream | null {
    return this.stream;
  }

  getVideoElement(): HTMLVideoElement | null {
    return this.videoElement;
  }

  stop() {
    this.isActive = false;
    if (this.intervalId) clearInterval(this.intervalId);
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    if (this.videoElement) this.videoElement.srcObject = null;
  }
}

// ─── Camera Feed Component for UI Thumbnail ────────────────────────────────────────────────
const CameraFeedThumbnail: React.FC<{
  stream: MediaStream | null;
  isActive: boolean;
}> = ({ stream, isActive }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  if (!isActive || !stream) {
    return (
      <div className="interview-session-camera-thumbnail-placeholder">
        <span>📷</span>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      className="interview-session-camera-thumbnail-video"
    />
  );
};

// ─── Camera Recorder Component ──────────────────────────────────────────────────
const CameraRecorder = React.memo<{
  onPermissionDenied: () => void;
  onCapture?: (imageData: string, timestamp: string) => void;
  onStreamReady?: (stream: MediaStream) => void;
  onVideoElementReady?: (videoElement: HTMLVideoElement) => void;
}>(({ onPermissionDenied, onCapture, onStreamReady, onVideoElementReady }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraManager = useRef(CameraManager.getInstance());
  const [isStarted, setIsStarted] = useState(false);

  useEffect(() => {
    cameraManager.current.setCallbacks((imageData, timestamp) => {
      onCapture?.(imageData, timestamp);
    }, onPermissionDenied);

    if (!isStarted) {
      cameraManager.current.start(videoRef, canvasRef);
      setIsStarted(true);

      const checkStream = setInterval(() => {
        const stream = cameraManager.current.getStream();
        if (stream) {
          onStreamReady?.(stream);
          clearInterval(checkStream);
        }
        const videoElement = cameraManager.current.getVideoElement();
        if (videoElement) onVideoElementReady?.(videoElement);
      }, 100);
    }
  }, [
    onPermissionDenied,
    onCapture,
    onStreamReady,
    onVideoElementReady,
    isStarted,
  ]);

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        bottom: 0,
        right: 0,
        width: "1px",
        height: "1px",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ width: "160px", height: "120px", display: "block" }}
      />
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
});

CameraRecorder.displayName = "CameraRecorder";

// ─── Malpractice Banner Component (Toast / Banner Above Chat) ─────────────────
const MalpracticeBanner: React.FC<{
  type: MalpracticeType;
  detectedObjects?: string[];
  details?: string;
  onDismiss: () => void;
  autoDismiss?: boolean;
}> = ({
  type,
  detectedObjects = [],
  details,
  onDismiss,
  autoDismiss = true,
}) => {
    useEffect(() => {
      if (autoDismiss) {
        const timer = setTimeout(onDismiss, 5000);
        return () => clearTimeout(timer);
      }
    }, [autoDismiss, onDismiss]);

    const getMessage = () => {
      switch (type) {
        case "multiple_faces":
          return {
            title: "Multiple Faces Detected",
            message: "Only one person should be visible in camera.",
            icon: "👥",
          };
        case "head_position":
          return {
            title: "Incorrect Head Position",
            message: "Please face the camera directly.",
            icon: "🎯",
          };
        case "looking_away":
          return {
            title: "Looking Away Detected",
            message: "Please maintain eye contact with the screen.",
            icon: "👁️",
          };
        case "no_face":
          return {
            title: "Face Not Detected",
            message: "Please ensure your face is clearly visible.",
            icon: "👤",
          };
        case "inactivity":
          return {
            title: "Inactivity Detected",
            message: "Please interact or speak to continue.",
            icon: "⏰",
          };
        case "prohibited_object":
          return {
            title: "Prohibited Object Detected",
            message: `Please remove prohibited object: ${detectedObjects.join(", ")}.`,
            icon: "📱",
          };
        case "api_malpractice":
          return {
            title: "Malpractice Detected",
            message: details || "Suspicious activity detected in the interview.",
            icon: "🚨",
          };
        default:
          return {
            title: "Proctor Warning",
            message: "Please follow the interview guidelines.",
            icon: "⚠️",
          };
      }
    };

    const { title, message, icon } = getMessage();

    return (
      <div className="malpractice-banner" role="alert" aria-live="polite">
        <div className="malpractice-banner-left">
          <div className="malpractice-banner-icon-badge">
            <span>{icon}</span>
          </div>
          <div className="malpractice-banner-text">
            <div className="malpractice-banner-title-row">
              <span className="malpractice-banner-title">⚠️ {title}</span>
              <span className="malpractice-banner-tag">Proctor Alert</span>
            </div>
            <p className="malpractice-banner-message">{message}</p>
          </div>
        </div>
        <div className="malpractice-banner-actions">
          <button
            type="button"
            onClick={onDismiss}
            className="malpractice-banner-dismiss-btn"
            aria-label="Dismiss alert"
            title="Dismiss"
          >
            ✕
          </button>
        </div>
        <div className="malpractice-banner-progress-bar" />
      </div>
    );
  };

const MalpracticePopup = MalpracticeBanner;

// ─── History Modal Component ──────────────────────────────────────────────────────
const HistoryModal: React.FC<{
  show: boolean;
  onClose: () => void;
  messages: ChatMessage[];
}> = ({ show, onClose, messages }) => {
  if (!show) return null;

  return (
    <div className="interview-session-history-overlay" onClick={onClose}>
      <div
        className="interview-session-history-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="interview-session-history-header">
          <h3>📜 Conversation History</h3>
          <button className="interview-session-history-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="interview-session-history-content">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`interview-session-history-item ${msg.role}`}
            >
              <div className="interview-session-history-role">
                {msg.role === "bot" ? "🤖 JozAI" : "👤 You"}
              </div>
              <div className="interview-session-history-text">{msg.text}</div>
              <div className="interview-session-history-time">
                {formatTime(msg.timestamp)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Helper function to append transcription segments without repeating overlapping words
const appendTranscript = (prevText: string, incomingText: string): string => {
  const prev = (prevText || "").trim();
  const incoming = (incomingText || "").trim();
  if (!incoming) return prev;
  if (!prev) return incoming;

  // Exact match or prev already ends with incoming
  if (
    prev.toLowerCase() === incoming.toLowerCase() ||
    prev.toLowerCase().endsWith(incoming.toLowerCase())
  ) {
    return prev;
  }

  // Incoming starts with prev (incoming is fuller utterance)
  if (incoming.toLowerCase().startsWith(prev.toLowerCase())) {
    return incoming;
  }

  // Check word-level overlap at the seam
  const prevWords = prev.split(/\s+/);
  const incomingWords = incoming.split(/\s+/);
  const maxOverlap = Math.min(prevWords.length, incomingWords.length);
  let overlap = 0;

  for (let i = maxOverlap; i > 0; i--) {
    const prevSlice = prevWords
      .slice(prevWords.length - i)
      .join(" ")
      .toLowerCase()
      .replace(/[.,?!]/g, "");
    const incomingSlice = incomingWords
      .slice(0, i)
      .join(" ")
      .toLowerCase()
      .replace(/[.,?!]/g, "");
    if (prevSlice === incomingSlice) {
      overlap = i;
      break;
    }
  }

  if (overlap > 0) {
    const nonOverlapping = incomingWords.slice(overlap).join(" ");
    return nonOverlapping ? `${prev} ${nonOverlapping}` : prev;
  }

  return `${prev} ${incoming}`;
};

// ─── Main Component ───────────────────────────────────────────────────────────────
const InterviewSessionPage: React.FC<InterviewSessionPageProps> = ({
  id: propId,
  isResume = false,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const sessionId = propId || paramId || "";
  const navigate = useNavigate();
  const api = useApi();
  const service = useMemo(() => new InterviewHubApiService(api), [api]);

  // ── Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [answer, setAnswer] = useState("");
  const answerRef = useRef("");
  const partialTextRef = useRef("");
  const isRecordingRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const [currentPhase, setCurrentPhase] = useState("");
  const [isFinished, setIsFinished] = useState(false);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isMicStarting, setIsMicStarting] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
  const [absenceCount, setAbsenceCount] = useState(0);
  const [showAbsencePopup, setShowAbsencePopup] = useState(false);
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Camera capture tracking state
  const [capturedImages, setCapturedImages] = useState<
    Array<{ timestamp: string; imageData: string }>
  >([]);
  const [lastCaptureTime, setLastCaptureTime] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null,
  );

  // Proctor API state
  const [isIntensiveMode, setIsIntensiveMode] = useState(false);
  const intensiveModeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const apiIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Malpractice state
  const [malpracticePopup, setMalpracticePopup] = useState<{
    show: boolean;
    type: MalpracticeType;
    detectedObjects?: string[];
    details?: string;
  }>({ show: false, type: "no_face" });

  const isBotThinkingRef = useRef(false);
  const objectDetectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ── Icebreaker state
  const [icebreakerState, setIcebreakerState] =
    useState<IcebreakerState>("polling");
  const [icebreakerFailReason, setIcebreakerFailReason] = useState("");

  // ── WebSocket / transcription
  const [wsStatus, setWsStatus] = useState<WsStatus>("idle");
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [partialText, setPartialText] = useState("");
  const transcribingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isConnectingRef = useRef(false);

  // Face detection
  const {
    isFaceDetected,
    isMultiFaceDetected,
    isPositionWrong,
    positionWarning,
  } = useFaceDetection(
    cameraStream,
    icebreakerState === "ready" && !isFinished,
  );

  // Object detection for prohibited items
  const { isObjectDetected, detectedObjects } = useObjectDetection(
    cameraStream,
    icebreakerState === "ready" && !isFinished,
  );

  // Function to capture frame as blob for API
  const captureFrameAsBlob = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (
        !videoElement ||
        videoElement.readyState < 2 ||
        videoElement.videoWidth === 0
      ) {
        resolve(null);
        return;
      }
      try {
        const canvas = document.createElement("canvas");
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(videoElement, 0, 0);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.8);
      } catch (error) {
        resolve(null);
      }
    });
  }, [videoElement]);

  // Function to call proctor API
  const callProctorAPI = useCallback(async () => {
    if (!sessionId || isFinished) return;
    const frameBlob = await captureFrameAsBlob();
    if (!frameBlob) return;
    try {
      const formData = new FormData();
      formData.append("file", frameBlob, `frame_${Date.now()}.jpg`);
      const response = await api.post("/proctor/upload-frame", formData, {
        params: { session_id: sessionId, timestamp: new Date().toISOString() },
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = response.data as ProctorAPIResponse;
      if (data.malpractice_detected) {
        setMalpracticePopup({
          show: true,
          type: "api_malpractice",
          details: data.details || undefined,
        });
        if (!isIntensiveMode) {
          setIsIntensiveMode(true);
          if (apiIntervalRef.current) clearInterval(apiIntervalRef.current);
          intensiveModeTimerRef.current = setTimeout(
            () => setIsIntensiveMode(false),
            INTENSIVE_MODE_DURATION_MS,
          );
        }
        setTimeout(
          () => setMalpracticePopup((prev) => ({ ...prev, show: false })),
          5000,
        );
      }
    } catch (error) {
      console.error("Proctor API call failed:", error);
    }
  }, [sessionId, isFinished, captureFrameAsBlob, api, isIntensiveMode]);

  // Setup API interval based on mode
  const setupApiInterval = useCallback(() => {
    if (apiIntervalRef.current) clearInterval(apiIntervalRef.current);
    const intervalMs = isIntensiveMode
      ? INTENSIVE_API_INTERVAL_MS
      : NORMAL_API_INTERVAL_MS;
    apiIntervalRef.current = setInterval(() => callProctorAPI(), intervalMs);
  }, [isIntensiveMode, callProctorAPI]);

  useEffect(() => {
    if (icebreakerState === "ready" && !isFinished && videoElement)
      setupApiInterval();
    return () => {
      if (apiIntervalRef.current) clearInterval(apiIntervalRef.current);
      if (intensiveModeTimerRef.current)
        clearTimeout(intensiveModeTimerRef.current);
    };
  }, [icebreakerState, isFinished, videoElement, setupApiInterval]);

  // Handle camera capture
  const handleCameraCapture = useCallback(
    (imageData: string, timestamp: string) => {
      setCapturedImages((prev) => [...prev, { timestamp, imageData }]);
      setLastCaptureTime(timestamp);
    },
    [],
  );

  // Handle face detection violations
  useEffect(() => {
    if (icebreakerState !== "ready" || isFinished) return;
    let timeoutId: NodeJS.Timeout;
    if (isMultiFaceDetected)
      setMalpracticePopup({ show: true, type: "multiple_faces" });
    else if (isPositionWrong)
      setMalpracticePopup({ show: true, type: "head_position" });
    else if (!isFaceDetected)
      setMalpracticePopup({ show: true, type: "no_face" });
    else return;
    timeoutId = setTimeout(
      () => setMalpracticePopup((prev) => ({ ...prev, show: false })),
      5000,
    );
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    isMultiFaceDetected,
    isPositionWrong,
    isFaceDetected,
    icebreakerState,
    isFinished,
  ]);

  // Handle object detection violations
  useEffect(() => {
    if (icebreakerState !== "ready" || isFinished) return;
    if (isObjectDetected && detectedObjects.length > 0) {
      setMalpracticePopup({
        show: true,
        type: "prohibited_object",
        detectedObjects,
      });
      objectDetectionTimeoutRef.current = setTimeout(
        () => setMalpracticePopup((prev) => ({ ...prev, show: false })),
        5000,
      );
    }
    return () => {
      if (objectDetectionTimeoutRef.current)
        clearTimeout(objectDetectionTimeoutRef.current);
    };
  }, [isObjectDetected, detectedObjects, icebreakerState, isFinished]);

  // Enable camera when session is ready
  useEffect(() => {
    if (icebreakerState === "ready" && !isFinished) setCameraReady(true);
  }, [icebreakerState, isFinished]);

  // ── Heartbeat & Duration Tracking
  useEffect(() => {
    if (icebreakerState !== "ready" || isFinished) return;
    const interval = setInterval(
      () => service.updateHeartbeat(sessionId, 20).catch(console.error),
      20000,
    );
    return () => clearInterval(interval);
  }, [icebreakerState, isFinished, sessionId]);

  // ── Activity Tracking
  const resetActivityTimer = useCallback(() => {
    setLastActivityTime(Date.now());
    if (showAbsencePopup) setShowAbsencePopup(false);
  }, [showAbsencePopup]);

  useEffect(() => setLastActivityTime(Date.now()), [icebreakerState]);

  useEffect(() => {
    if (icebreakerState !== "ready" || isFinished || isBotThinkingRef.current)
      return;
    const timer = setInterval(() => {
      const diff = Date.now() - lastActivityTime;
      if (diff > 180000) handleAutoClose();
      else if (diff > 60000 && !showAbsencePopup) setShowAbsencePopup(true);
    }, 2000);
    return () => clearInterval(timer);
  }, [icebreakerState, isFinished, lastActivityTime, showAbsencePopup]);

  const handleAutoClose = async () => {
    setIsFinished(true);
    setShowAbsencePopup(false);
    setIsBotTyping(true);
    Loader.showLoading("Ending session due to inactivity...");
    try {
      const resp = await service.submitAnswer(sessionId, {
        confirmed_answer: "[System: Interview terminated due to inactivity]",
        is_inactivity: true,
      });
      setIsBotTyping(false);
      pushMessage("bot", resp.message || "Interview closed due to inactivity.");
      if (resp.audio_url) playAudio(resp.audio_url);
    } catch (err) {
      console.error(err);
      setIsBotTyping(false);
    } finally {
      Loader.dismissLoading();
    }
  };

  // ── Auto-scroll chat
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBotTyping]);

  // ── Play audio helper
  const playAudio = useCallback((url: string) => {
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.play().catch(console.error);
    }
  }, []);

  // ── Add a message to chat
  const pushMessage = useCallback((role: "bot" | "user", text: string) => {
    setMessages((prev) => [
      ...prev,
      { id: makeId(), role, text, timestamp: new Date() },
    ]);
  }, []);

  // ── Transcription hook - FIXED
  const { connect, disconnect, startMicrophone, stopMicrophone } =
    useDirectTranscribe({
      onTranscript: ({ text, isPartial }) => {
        console.log("📝 Transcript received:", {
          text: text.substring(0, 100),
          isPartial,
        });
        resetActivityTimer();

        // Guard: Drop lingering/late transcripts if submission is in progress or bot is typing/thinking
        if (isSubmittingRef.current || isBotThinkingRef.current) {
          console.log("🛑 Discarding late transcript during submit/bot turn:", text);
          return;
        }

        if (isPartial) {
          setPartialText(text);
          partialTextRef.current = text;
        } else {
          console.log("✅ Final transcript received, length:", text.length);
          setAnswer((prev) => {
            if (isSubmittingRef.current || isBotThinkingRef.current) {
              return prev;
            }
            const current = prev || answerRef.current || "";
            const newAnswer = appendTranscript(current, text);
            answerRef.current = newAnswer;
            console.log("📝 Setting answer to length:", newAnswer.length);
            return newAnswer;
          });
          setPartialText("");
          partialTextRef.current = "";

          // Final transcribe arrived! Clear timeout and enable submit button
          if (transcribingTimeoutRef.current) {
            clearTimeout(transcribingTimeoutRef.current);
            transcribingTimeoutRef.current = null;
          }
          setIsTranscribing(false);
        }
      },
      onStatusChange: (status) => {
        console.log("🔊 WS Status:", status);
        if (status.toLowerCase().includes("connect")) {
          setWsStatus("connecting");
        } else if (status.toLowerCase().includes("connected")) {
          setWsStatus("connected");
        } else if (status.toLowerCase().includes("error")) {
          setWsStatus("error");
        } else if (status === "Disconnected") {
          setWsStatus("idle");
        }
      },
      onError: (err) => {
        console.error("❌ WS error:", err);
        setWsStatus("error");
      },
    });

  // SINGLE WebSocket connection effect - ONLY ONCE
  useEffect(() => {
    // Only connect once when icebreaker is ready and we haven't connected yet
    if (
      icebreakerState !== "ready" ||
      isConnectingRef.current ||
      wsStatus === "connected"
    ) {
      return;
    }

    const init = async () => {
      try {
        isConnectingRef.current = true;
        const url = await service.getTranscribeUrl(sessionId);
        console.log("🔊 Connecting to WebSocket:", url);
        connect(url);
      } catch (err) {
        console.error("Failed to connect WebSocket:", err);
        isConnectingRef.current = false;
      }
    };

    init();

    // Cleanup only when component unmounts or icebreaker state changes away from ready
    return () => {
      // Only disconnect if we're not ready anymore or component unmounts
      if (icebreakerState !== "ready") {
        isConnectingRef.current = false;
        disconnect();
      }
    };
  }, [icebreakerState]); // Remove sessionId, service, connect, disconnect from dependencies

  // Monitor WebSocket status and update connecting ref
  useEffect(() => {
    console.log("🔊 WebSocket status changed to:", wsStatus);
    if (wsStatus === "connected") {
      isConnectingRef.current = false;
    }
    if (wsStatus === "error") {
      isConnectingRef.current = false;
    }
  }, [wsStatus]);
  // Auto-log capture stats every 30 seconds
  useEffect(() => {
    if (icebreakerState !== "ready" || isFinished) return;
    const interval = setInterval(() => {
      console.log(`📊 [Auto Log] Total captures: ${capturedImages.length}`);
    }, 30000);
    return () => clearInterval(interval);
  }, [icebreakerState, isFinished, capturedImages.length]);

  // ── Stop mic / disconnect WS
  const stopRecording = useCallback(() => {
    setIsRecording(false);
    isRecordingRef.current = false;
    setIsSpeaking(false);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    stopMicrophone();

    // Immediately commit any pending live transcription so candidate doesn't lose text
    const pending = partialTextRef.current.trim();
    if (pending && !isSubmittingRef.current) {
      setAnswer((prev) => {
        const current = prev || answerRef.current || "";
        const merged = appendTranscript(current, pending);
        answerRef.current = merged;
        return merged;
      });
      setPartialText("");
      partialTextRef.current = "";
    }

    // Disable submit button briefly while waiting for trailing audio packets
    setIsTranscribing(true);

    if (transcribingTimeoutRef.current) {
      clearTimeout(transcribingTimeoutRef.current);
    }

    // Safety timeout: wait up to 800ms for final transcribe event to arrive
    transcribingTimeoutRef.current = setTimeout(() => {
      const latePending = partialTextRef.current.trim();
      if (latePending && !isSubmittingRef.current) {
        setAnswer((prev) => {
          const current = prev || answerRef.current || "";
          const merged = appendTranscript(current, latePending);
          answerRef.current = merged;
          return merged;
        });
        setPartialText("");
        partialTextRef.current = "";
      }
      setIsTranscribing(false);
    }, 800);
  }, [stopMicrophone]);

  // ── Start mic (reuse existing WS connection) - FIXED
  const startRecording = useCallback(async () => {
    if (isRecording) {
      console.log("Already recording");
      return;
    }

    if (transcribingTimeoutRef.current) {
      clearTimeout(transcribingTimeoutRef.current);
      transcribingTimeoutRef.current = null;
    }
    setIsTranscribing(false);

    try {
      console.log("🎤 Starting recording...");
      setIsMicStarting(true);

      const stream = await startMicrophone();
      console.log("🎤 Microphone stream obtained");
      streamRef.current = stream;

      setIsRecording(true);
      isRecordingRef.current = true;
      isSubmittingRef.current = false;
      resetActivityTimer();
      // Keep existing answer so additional speech appends to it seamlessly
      setPartialText("");
      partialTextRef.current = "";
    } catch (err) {
      console.error("🎤 Failed to start recording:", err);
      alert("Failed to access microphone. Please check your permissions.");
    } finally {
      setIsMicStarting(false);
    }
  }, [startMicrophone, resetActivityTimer, isRecording]);

  const handleStopRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // ── Icebreaker polling
  const isPollingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startIcebreakerPolling = useCallback(() => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    const poll = async () => {
      if (!isPollingRef.current) return;

      try {
        const res = await service.getIcebreaker(sessionId);
        if (res.status === "PENDING") {
          timeoutRef.current = setTimeout(poll, 2000);
          return;
        }

        isPollingRef.current = false;

        if (res.status === "READY" && res.icebreaker_text) {
          pushMessage("bot", res.icebreaker_text);
          setIcebreakerState("ready");
          if (res.audio_url) playAudio(res.audio_url);
        } else {
          setIcebreakerFailReason(res.reason || "Interview setup failed.");
          setIcebreakerState("failed");
        }
      } catch (err) {
        console.error("Icebreaker poll error:", err);
        if (isPollingRef.current) {
          timeoutRef.current = setTimeout(poll, 3000);
        }
      }
    };

    timeoutRef.current = setTimeout(poll, 2000);
  }, [sessionId, service, pushMessage, playAudio]);

  // ── Initial mount
  useEffect(() => {
    if (!sessionId) return;

    const init = async () => {
      if (isResume) {
        try {
          const data = await service.resumeInterview(sessionId);
          if (data.history && data.history.length > 0)
            setMessages(historyToMessages(data.history));
          if (data.next_question) pushMessage("bot", data.next_question);
          if (data.current_phase) setCurrentPhase(data.current_phase);
          setLastActivityTime(Date.now());
          setIcebreakerState("ready");
          if (data.audio_url) playAudio(data.audio_url);
        } catch (err) {
          console.error("Failed to resume interview:", err);
          setIcebreakerFailReason("Failed to resume interview.");
          setIcebreakerState("failed");
        }
      } else {
        startIcebreakerPolling();
      }
    };

    init();

    return () => {
      isPollingRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      stopRecording();
      if (apiIntervalRef.current) clearInterval(apiIntervalRef.current);
      if (intensiveModeTimerRef.current)
        clearTimeout(intensiveModeTimerRef.current);
    };
  }, [
    sessionId,
    isResume,
    service,
    pushMessage,
    playAudio,
    startIcebreakerPolling,
    stopRecording,
  ]);

  // ── Submit answer
  const handleSubmit = async () => {
    if (isSubmittingRef.current || isFinished) return;

    // 1. If still recording, stop mic and wait briefly for in-flight audio to transcribe
    if (isRecordingRef.current) {
      handleStopRecording();
      await new Promise((resolve) => setTimeout(resolve, 500));
    } else if (partialTextRef.current.trim()) {
      // If mic was just stopped but partialText is still pending, brief wait for final
      await new Promise((resolve) => setTimeout(resolve, 400));
    }

    // 2. Combine finalized answer with any lingering partial text
    let currentAnswer = answerRef.current.trim();
    const pendingPartial = partialTextRef.current.trim();
    if (pendingPartial) {
      currentAnswer = appendTranscript(currentAnswer, pendingPartial);
    }

    if (!currentAnswer) return;

    // 3. Mark as submitting - locks transcription stream so NO late chunks from this question pollute the next
    isSubmittingRef.current = true;
    isBotThinkingRef.current = true;

    // 4. Immediately clear fields for the next question
    setAnswer("");
    answerRef.current = "";
    setPartialText("");
    partialTextRef.current = "";

    pushMessage("user", currentAnswer);
    setIsBotTyping(true);
    resetActivityTimer();

    Loader.showLoading("Evaluating answer...");
    try {
      const resp = await service.submitAnswer(sessionId, {
        confirmed_answer: currentAnswer,
      });
      setIsBotTyping(false);
      isBotThinkingRef.current = false;
      resetActivityTimer();

      // Ensure answer remains blank for the next question
      setAnswer("");
      answerRef.current = "";
      setPartialText("");
      partialTextRef.current = "";

      if (resp.decision === "FINISH") {
        setIsFinished(true);
        pushMessage(
          "bot",
          resp.message || "Thank you for completing the interview!",
        );
      } else if (resp.next_question) {
        pushMessage("bot", resp.next_question);
        if (resp.audio_url) playAudio(resp.audio_url);
      }
    } catch (err) {
      console.error("Submit failed:", err);
      setIsBotTyping(false);
      isBotThinkingRef.current = false;
      pushMessage(
        "bot",
        "Sorry, something went wrong. Please try submitting again.",
      );
    } finally {
      isSubmittingRef.current = false;
      Loader.dismissLoading();
    }
  };

  // ── Enter key to submit
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // ── VA speaking detection
  useEffect(() => {
    if (!isRecording || !streamRef.current) return;
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)(
      { sampleRate: 16000 },
    );
    const src = ctx.createMediaStreamSource(streamRef.current);
    const analyser = ctx.createAnalyser();
    src.connect(analyser);
    const checkSpeaking = setInterval(() => {
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteTimeDomainData(dataArray);
      let maxSample = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] - 128) / 128;
        maxSample = Math.max(maxSample, Math.abs(v));
      }
      setIsSpeaking(maxSample > 0.05);
    }, 100);
    return () => {
      clearInterval(checkSpeaking);
      src.disconnect();
      ctx.close();
    };
  }, [isRecording]);

  const currentQuestion = messages.filter((m) => m.role === "bot").pop();

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="interview-session-container">
      <audio ref={audioRef} style={{ display: "none" }} />

      {/* History Modal */}
      <HistoryModal
        show={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        messages={messages}
      />

      {/* Camera Recorder */}
      {cameraReady && (
        <CameraRecorder
          onPermissionDenied={() => setCameraPermissionDenied(true)}
          onCapture={handleCameraCapture}
          onStreamReady={(stream) => setCameraStream(stream)}
          onVideoElementReady={(video) => setVideoElement(video)}
        />
      )}

      {/* Camera permission warning */}
      {cameraPermissionDenied && (
        <div className="interview-session-camera-warning">
          ⚠️ Camera access denied. Proctoring will continue without video.
        </div>
      )}

      {/* ── Header */}
      <div className="interview-session-header">
        <button
          className="interview-session-back-btn"
          onClick={() => navigate("/dashboard/interview-hub")}
        >
          ← Back
        </button>

        <div className="interview-session-title-area">
          <div className="interview-session-title">
            Interview - {currentPhase || "HR Round"}
          </div>
        </div>

        <div className="interview-session-header-actions">
          {/* Camera Thumbnail */}
          <div className="interview-session-camera-thumbnail">
            <CameraFeedThumbnail
              stream={cameraStream}
              isActive={cameraReady && !cameraPermissionDenied}
            />
          </div>

          <button
            className="interview-session-history-btn"
            onClick={() => setShowHistoryModal(true)}
          >
            📜 History
          </button>

          <div className="interview-session-status">
            <div
              className={`interview-session-status-dot ${wsStatus === "connected" ? "connected" : "connecting"}`}
            />
            <span>{wsStatus === "connected" ? "Live" : "Connecting..."}</span>
          </div>
        </div>
      </div>

      {/* ── Malpractice Banner / Toast Above Chat (Non-blocking) */}
      {malpracticePopup.show && (
        <MalpracticeBanner
          type={malpracticePopup.type}
          detectedObjects={malpracticePopup.detectedObjects}
          details={malpracticePopup.details}
          onDismiss={() =>
            setMalpracticePopup((prev) => ({ ...prev, show: false }))
          }
        />
      )}

      {/* ── Icebreaker Overlay */}
      {icebreakerState === "polling" && (
        <div className="interview-session-icebreaker-overlay">
          <div className="interview-session-icebreaker-spinner" />
          <div className="interview-session-icebreaker-title">
            JozAI is preparing your interview…
          </div>
          <div className="interview-session-icebreaker-subtitle">
            We're tailoring questions based on your resume. This usually takes
            10–20 seconds.
          </div>
        </div>
      )}

      {icebreakerState === "failed" && (
        <div className="interview-session-icebreaker-overlay">
          <div className="interview-session-icebreaker-failed">
            <h3>Interview Setup Failed</h3>
            <p>{icebreakerFailReason}</p>
          </div>
          <button
            className="interview-session-back-btn"
            onClick={() => navigate("/dashboard/interview-hub")}
          >
            ← Back to Hub
          </button>
        </div>
      )}

      {/* ── Main Content - Two Column Layout */}
      {icebreakerState === "ready" && (
        <div className="interview-session-two-column">
          {/* Left Column - Question */}
          <div className="interview-session-question-column">
            {currentQuestion && (
              <div
                key={currentQuestion.id}
                className="interview-session-question-card"
              >
                <div className="interview-session-question-header">
                  <span className="interview-session-jozagent-icon">🤖</span>
                  <span className="interview-session-jozagent-label">
                    JozAgent
                  </span>
                  <span className="interview-session-question-number">
                    Question No.{" "}
                    {messages.filter((m) => m.role === "bot").length}
                  </span>
                </div>
                <div className="interview-session-question-text">
                  {currentQuestion.text}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Answer Area */}
          <div className="interview-session-answer-column">
            <div className="interview-session-input-header">
              <h3 className="interview-session-input-title">Give Your Reply</h3>
            </div>

            {/* Live Transcription Display while recording */}
            {isRecording && (
              <div className="interview-session-live-transcription">
                <div className="interview-session-live-transcription-header">
                  <span className="recording-dot">🔴</span>
                  <span>Live Transcription</span>
                </div>
                <div className="interview-session-live-transcription-text">
                  {partialText ||
                    (isSpeaking ? "Listening..." : "Say something...")}
                </div>
              </div>
            )}

            {/* Textarea for answer */}
            <textarea
              className="interview-session-textarea"
              value={answer}
              onChange={(e) => {
                setAnswer(e.target.value);
                answerRef.current = e.target.value;
                resetActivityTimer();
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                isRecording
                  ? "Recording... Your transcribed text will appear above. Click Stop Recording when done."
                  : "Type your answer here or click Record Voice to speak..."
              }
              rows={6}
            />

            {/* Controls */}
            <div className="interview-session-input-controls">
              <div className="interview-session-recording-controls">
                <button
                  className={`interview-session-mic-btn ${isRecording ? "recording" : ""}`}
                  onClick={isRecording ? handleStopRecording : startRecording}
                  disabled={isMicStarting}
                >
                  {isRecording ? "⏹ Stop Recording" : "🎤 Record Voice"}
                </button>
                <span className="interview-session-recording-hint">
                  {isRecording
                    ? isSpeaking
                      ? "🔴 Speaking detected..."
                      : "🎤 Microphone active - speak now"
                    : wsStatus === "connected"
                      ? "Click to record your voice"
                      : "Connecting..."}
                </span>
              </div>

              <div className="interview-session-text-hint">
                Type your reply as text or edit the transcribed voice response.
              </div>

              <button
                className="interview-session-submit-btn"
                onClick={handleSubmit}
                disabled={!answer.trim() || isRecording || isTranscribing || isBotTyping}
              >
                {isTranscribing ? "⏳ Transcribing..." : "Submit Reply"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completion banner */}
      {isFinished && (
        <div className="interview-session-completion-banner">
          <h3>🎉 Interview Complete!</h3>
          <p>
            We're processing your responses. Your results will be shared with
            you shortly.
          </p>
        </div>
      )}

      {/* Absence Nudge Popup */}
      {showAbsencePopup && (
        <div className="interview-session-absence-overlay">
          <div className="interview-session-absence-modal">
            <div className="interview-session-absence-icon">🕒</div>
            <h3>Are you there?</h3>
            <p>We noticed some inactivity. Are you here?</p>
            <button
              className="interview-session-absence-btn"
              onClick={resetActivityTimer}
            >
              Yes, I'm here
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewSessionPage;
